import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, BookOpen, FileText, PenTool, ClipboardCheck, Calendar, Gamepad2, Star, ShieldCheck, ChevronDown, AlertCircle, Download, ArrowRight, Sparkles, X, Lock } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { CountdownTimer } from '../components/CountdownTimer';
import { useLocalCurrency } from '../hooks/useLocalCurrency';
import { useCountdown } from '../hooks/useCountdown';
// Product Images
const IMAGES = {
  cover: 'https://cdn.magicpatterns.com/uploads/c3dWU3DvqMuC37hAK3gshj/Portada_1.png',
  bono1: 'https://cdn.magicpatterns.com/uploads/1zCf1r8MJStmwtFsyBaCpa/Portada_2.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/dHy4NE5ftrAGoHi9wsQMNt/Portada_3.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/tRA7quvK434qBD76H1ZV6D/Portada_4.png',
  bono4: 'https://cdn.magicpatterns.com/uploads/8RJBDfR7nFMtQfm2u99B7y/Portada_5.png',
  megapack: 'https://cdn.magicpatterns.com/uploads/hGACu9XX9CwWwkWTGzDEAd/ChatGPT_Image_Mar_5,_2026,_12_39_42_AM.png'
};
const CHECKOUT_URL = 'https://pay.hotmart.com/Q103480014Q?checkoutMode=10';
const TESTIMONIALS = [{
  name: 'M. A.',
  location: 'Argentina',
  role: 'Secundaria',
  text: 'Me ordenó el año. Las consignas están súper claras y no siento que tengo que inventar todo.'
}, {
  name: 'P. R.',
  location: 'Chile',
  role: 'Primaria',
  text: 'Las actividades de comprensión lectora son exactamente lo que necesitaba: listas y bien pensadas.'
}, {
  name: 'L. G.',
  location: 'México',
  role: 'Prof. particular',
  text: 'Lo uso para preparar clases en minutos. Me salvó con alumnos de distintos niveles.'
}, {
  name: 'S. D.',
  location: 'Colombia',
  role: 'Secundaria',
  text: 'Las rúbricas son oro. Corregí mucho más rápido y con criterios claros.'
}, {
  name: 'A. M.',
  location: 'Perú',
  role: 'Primaria',
  text: 'Todo está ordenado y sirve de verdad. No es relleno.'
}];
const FAQS = [{
  q: '¿Es imprimible?',
  a: 'Sí. Está diseñado para imprimir y usar directamente en clase.'
}, {
  q: '¿Viene organizado por unidades/temas?',
  a: 'Sí. Está organizado por carpetas y categorías (leer, escribir, gramática, evaluar).'
}, {
  q: '¿Incluye rúbricas / criterios de corrección?',
  a: 'Sí. Incluye rúbricas y criterios listos para aplicar.'
}, {
  q: '¿Es editable?',
  a: 'Incluye versiones editables cuando corresponde y es fácilmente adaptable.'
}, {
  q: '¿Cómo lo descargo?',
  a: 'Acceso inmediato al finalizar la compra. Recibís el enlace de descarga en tu correo.'
}, {
  q: '¿Y si no me sirve?',
  a: 'Tenés 7 días de garantía total. Lo probás y si no te convence, pedís el reembolso completo. Sin preguntas.'
}, {
  q: '¿Trae contenido útil o relleno?',
  a: 'Trae material pensado para usar: textos, actividades, consignas, evaluaciones y rúbricas. Nada de relleno.'
}];
const MODULES = [{
  title: '1) Banco de Textos + Comprensión Lectora',
  icon: <BookOpen className="w-6 h-6" data-id="element-24" />,
  items: ['Textos narrativos, informativos y argumentativos', 'Preguntas literales, inferenciales y críticas', 'Consignas claras y aplicables', 'Actividades por niveles (adaptables)']
}, {
  title: '2) Guías de Gramática y Ortografía',
  icon: <FileText className="w-6 h-6" data-id="element-25" />,
  items: ['Explicación breve y directa', 'Ejercicios listos para imprimir', 'Refuerzo y repaso']
}, {
  title: '3) Producción Escrita (por tipo textual)',
  icon: <PenTool className="w-6 h-6" data-id="element-26" />,
  items: ['Consignas por tipo de texto (cuento, noticia, carta, etc.)', 'Plantillas para guiar la escritura', 'Checklists para revisar antes de entregar']
}, {
  title: '4) Rúbricas y Criterios de Corrección Listos',
  icon: <ClipboardCheck className="w-6 h-6" data-id="element-27" />,
  items: ['Rúbricas ajustables', 'Criterios claros (contenido, coherencia, ortografía)', 'Formatos rápidos para evaluación']
}, {
  title: '5) Evaluaciones Listas por Unidad / Tema',
  icon: <Check className="w-6 h-6" data-id="element-28" />,
  items: ['Pruebas preparadas con consignas claras', 'Adaptables', 'Pauta/clave orientativa incluida']
}, {
  title: '6) Planificación Anual + Semanales',
  icon: <Calendar className="w-6 h-6" data-id="element-29" />,
  items: ['Estructura anual sugerida', 'Plantillas semanales listas para completar', 'Registro y seguimiento']
}, {
  title: '7) Juegos y Actividades Dinámicas',
  icon: <Gamepad2 className="w-6 h-6" data-id="element-30" />,
  items: ['Dinámicas para lectura y escritura', 'Actividades participativas', 'Recursos para aula y grupos']
}];
const BONUSES = [{
  title: 'BONO 1 – Kit de Evaluaciones Premium',
  desc: 'Evaluaciones listas con criterios y pautas para corregir más rápido.',
  image: IMAGES.bono3,
  value: '50+ pruebas'
}, {
  title: 'BONO 2 – Banco de Juegos de Lectura y Escritura',
  desc: 'Recursos lúdicos para motivar y sostener la participación.',
  image: IMAGES.bono2,
  value: '30+ dinámicas'
}, {
  title: 'BONO 3 – Plantillas Profesionales Docentes',
  desc: 'Calendario, registro, seguimiento, organización anual y planillas.',
  image: IMAGES.bono4,
  value: 'Organización total'
}, {
  title: 'BONO EXTRA – Corrector Express',
  desc: 'Banco de rúbricas + frases de retroalimentación.',
  image: IMAGES.bono1,
  value: 'Feedback rápido'
}];
// Value stack items with USD values for the pricing section
const VALUE_STACK = [{
  label: '5 Tomos Completos de Lengua y Literatura',
  value: 120,
  highlight: true
}, {
  label: 'Banco de Textos + Comprensión Lectora',
  value: 35
}, {
  label: 'Guías de Gramática y Ortografía',
  value: 25
}, {
  label: 'Producción Escrita por Tipo Textual',
  value: 30
}, {
  label: 'Rúbricas y Criterios de Corrección',
  value: 25
}, {
  label: 'Evaluaciones Listas por Unidad',
  value: 30
}, {
  label: 'Planificación Anual + Semanales',
  value: 20
}, {
  label: 'BONO: Kit de Evaluaciones Premium',
  value: 35,
  isBonus: true
}, {
  label: 'BONO: Banco de Juegos',
  value: 25,
  isBonus: true
}, {
  label: 'BONO: Plantillas Profesionales',
  value: 20,
  isBonus: true
}, {
  label: 'BONO: Corrector Express',
  value: 15,
  isBonus: true
}];
export function LandingPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const {
    minutes,
    seconds
  } = useCountdown();
  const {
    formatPrice,
    currencyCode,
    countryFlag,
    isUSD,
    isLoading: currencyLoading
  } = useLocalCurrency();
  useEffect(() => {
    const compraSection = document.getElementById('compra');
    if (!compraSection) return;
    const observer = new IntersectionObserver(([entry]) => {
      setShowStickyCTA(entry.isIntersecting || entry.boundingClientRect.top < 0);
    }, {
      threshold: 0
    });
    observer.observe(compraSection);
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const totalValue = VALUE_STACK.reduce((sum, item) => sum + item.value, 0);
  const savingsPercent = Math.round((1 - 13.99 / 47) * 100);
  return <div className="min-h-screen bg-[#FDF8F3] font-sans text-[#2D2A26] overflow-x-hidden pb-20 md:pb-0" data-id="element-31">
      <CountdownTimer data-id="element-32" />
      <PurchaseNotification data-id="element-33" />

      {/* Sticky Mobile CTA */}
      <AnimatePresence data-id="element-34">
        {showStickyCTA && <motion.div initial={{
        y: 100
      }} animate={{
        y: 0
      }} exit={{
        y: 100
      }} className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white/95 backdrop-blur-sm border-t border-[#E8A838]/20 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] md:hidden" data-id="element-35">
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full bg-[#C75B39] text-white font-bold py-4 px-6 rounded-xl shadow-lg animate-pulse text-lg" data-id="element-36">
              ¡QUIERO EL MEGAPACK YA!
            </a>
          </motion.div>}
      </AnimatePresence>

      {/* Hero Section */}
      <header className="bg-gradient-to-br from-[#1B5E4B] via-[#2D7A65] to-[#1B5E4B] text-white pt-32 pb-24 px-4 relative overflow-hidden" data-id="element-37">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[url('https://www.transparenttextures.com/patterns/notebook.png')]" data-id="element-38"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#E8A838] opacity-10 rounded-full blur-[100px]" data-id="element-39"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-40">
          <motion.div initial={{
          opacity: 0,
          y: -30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center" data-id="element-41">
            <div className="inline-block bg-[#E8A838] text-[#1B5E4B] font-bold px-4 py-1.5 rounded-full text-sm mb-8 shadow-lg transform -rotate-1 tracking-wide" data-id="element-42">
              ¡NUEVO 2026!
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 max-w-4xl mx-auto" data-id="element-43">
              Lengua y Literatura{' '}
              <span className="text-[#E8A838] italic" data-id="element-44">
                listas para todo el año
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-emerald-100 mb-6 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-45">
              Sin perder horas planificando. Actividades, evaluaciones y
              rúbricas listas para imprimir y usar.
            </p>

            {/* Social Proof Counter */}
            <div className="flex items-center justify-center gap-3 mb-12" data-id="element-46">
              <div className="flex -space-x-2" data-id="element-47">
                {['M', 'A', 'L', 'S', 'P'].map((letter, i) => <div key={i} className="w-8 h-8 rounded-full bg-emerald-300/30 border-2 border-white/30 flex items-center justify-center text-xs font-bold text-white" data-id="element-48">
                    {letter}
                  </div>)}
              </div>
              <div className="flex items-center gap-1.5" data-id="element-49">
                <div className="flex gap-0.5" data-id="element-50">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-3.5 h-3.5 text-[#E8A838] fill-current" data-id="element-51" />)}
                </div>
                <span className="text-sm text-emerald-100 font-medium" data-id="element-52">
                  +2.400 docentes ya lo usan
                </span>
              </div>
            </div>
          </motion.div>

          {/* Book Fan Layout */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative flex items-center justify-center h-[250px] md:h-[420px] mb-8 max-w-4xl mx-auto" data-id="element-53">
            <div className="absolute inset-0 bg-[#E8A838] opacity-15 blur-[100px] rounded-full" data-id="element-54"></div>
            <img src={IMAGES.bono1} alt="Bono 1" className="absolute w-24 md:w-40 z-10 transform -rotate-12 -translate-x-28 md:-translate-x-60 drop-shadow-[0_15px_30px_rgba(0,0,0,0.4)] transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-55" />
            <img src={IMAGES.bono2} alt="Bono 2" className="absolute w-28 md:w-48 z-20 transform -rotate-6 -translate-x-16 md:-translate-x-32 drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)] transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-56" />
            <img src={IMAGES.cover} alt="Main Cover" className="relative w-36 md:w-60 z-30 drop-shadow-[0_30px_60px_rgba(0,0,0,0.5)] transition-transform duration-500" data-id="element-57" />
            <img src={IMAGES.bono3} alt="Bono 3" className="absolute w-28 md:w-48 z-20 transform rotate-6 translate-x-16 md:translate-x-32 drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)] transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-58" />
            <img src={IMAGES.bono4} alt="Bono 4" className="absolute w-24 md:w-40 z-10 transform rotate-12 translate-x-28 md:translate-x-60 drop-shadow-[0_15px_30px_rgba(0,0,0,0.4)] transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-59" />
          </motion.div>

          <div className="text-center relative z-40" data-id="element-60">
            <div className="inline-block bg-white/10 backdrop-blur-md border border-white/20 text-white font-medium px-6 py-2.5 rounded-full text-sm shadow-xl" data-id="element-61">
              📚 5 TOMOS + 4 BONOS INCLUIDOS
            </div>
            <div className="mt-10" data-id="element-62">
              <button onClick={scrollToPurchase} className="bg-[#C75B39] hover:bg-[#A63E28] text-white font-bold py-5 px-10 rounded-xl shadow-[0_10px_30px_rgba(199,91,57,0.4)] transform transition hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(199,91,57,0.5)] text-lg md:text-xl inline-flex items-center justify-center gap-3 border-b-4 border-[#8B3A22] cursor-pointer" data-id="element-63">
                <Download className="w-5 h-5" data-id="element-64" />
                Quiero el MegaPack Ahora
              </button>
              <p className="mt-4 text-sm text-emerald-200/80 font-medium" data-id="element-65">
                Acceso inmediato · Garantía 7 días
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Benefits Checklist Bar */}
      <section className="bg-white py-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative z-20 -mt-8 mx-4 md:mx-auto max-w-5xl rounded-2xl border border-gray-100" data-id="element-66">
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 text-[#2D2A26] font-medium text-sm md:text-base px-6" data-id="element-67">
          {['Ahorrá horas de planificación', 'Textos + consignas listas', 'Evaluaciones con pautas', 'Rúbricas listas para corregir'].map((text, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-68">
              <div className="bg-emerald-50 p-1.5 rounded-full" data-id="element-69">
                <Check className="w-4 h-4 text-[#2D7A65]" data-id="element-70" />
              </div>
              {text}
            </div>)}
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-4 max-w-4xl mx-auto" data-id="element-71">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-white p-10 md:p-16 rounded-3xl shadow-[0_8px_40px_rgb(0,0,0,0.04)] border border-gray-100 relative overflow-hidden" data-id="element-72">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#C75B39] to-[#E8A838]" data-id="element-73"></div>

          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2D2A26] mb-10 text-center leading-tight" data-id="element-74">
            Si das Lengua y Literatura, <br className="hidden md:block" data-id="element-75" />{' '}
            seguro te pasó:
          </h2>

          <div className="prose prose-lg max-w-none text-gray-600" data-id="element-76">
            <p className="mb-6" data-id="element-77">
              Te sentás a planificar "un ratito"… y cuando levantás la cabeza ya
              pasó media tarde.
            </p>
            <p className="mb-8 font-medium text-[#2D2A26]" data-id="element-78">
              No porque seas lenta. No porque no sepas. Sino porque Lengua exige
              demasiado:
            </p>

            <ul className="space-y-5 mb-10" data-id="element-79">
              {['Buscar textos adecuados', 'Crear consignas que realmente funcionen', 'Adaptar niveles (porque no todos están al mismo ritmo)', 'Armar actividades claras (sin que se vuelva relleno)', 'Pensar evaluaciones', 'Y encima… corregir escritura (que consume energía mental real)'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-80">
                  <div className="bg-orange-50 p-1 rounded-full mt-0.5 flex-shrink-0" data-id="element-81">
                    <AlertCircle className="w-5 h-5 text-[#C75B39]" data-id="element-82" />
                  </div>
                  <span className="text-gray-700 leading-relaxed" data-id="element-83">{item}</span>
                </li>)}
            </ul>

            <div className="bg-[#F5F0EA] p-8 rounded-2xl border-l-4 border-[#2D7A65] relative" data-id="element-84">
              <div className="absolute -top-4 -left-2 text-6xl text-[#2D7A65]/20 font-heading" data-id="element-85">
                "
              </div>
              <p className="italic text-gray-700 relative z-10 m-0 text-lg" data-id="element-86">
                Nadie debería sostener un año entero de Lengua inventando
                material cada semana. Porque mientras planificás, buscás y
                corregís… la lista de pendientes sigue creciendo.
              </p>
            </div>

            <div className="text-center mt-10" data-id="element-87">
              <button onClick={scrollToPurchase} className="text-[#C75B39] hover:text-[#A63E28] font-bold text-lg inline-flex items-center gap-2 transition group cursor-pointer" data-id="element-88">
                Quiero dejar de improvisar
                <span className="group-hover:translate-x-1 transition-transform" data-id="element-89">
                  →
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* NUEVO: Before / After Section */}
      <section className="py-16 px-4 bg-white border-b border-gray-100" data-id="element-90">
        <div className="max-w-5xl mx-auto" data-id="element-91">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-[#2D2A26] mb-12" data-id="element-92">
            Tu semana docente: <span className="text-[#C75B39]" data-id="element-93">antes</span> vs{' '}
            <span className="text-[#2D7A65]" data-id="element-94">después</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-id="element-95">
            {/* Before */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-red-50/50 rounded-2xl p-8 border border-red-100/50 relative" data-id="element-96">
              <div className="absolute top-4 right-4 bg-[#C75B39]/10 text-[#C75B39] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-97">
                Sin el MegaPack
              </div>
              <div className="space-y-4 mt-6" data-id="element-98">
                {['Domingo a la noche buscando textos en Google', '3+ horas armando una evaluación desde cero', 'Corregir sin criterios claros (agotamiento)', 'Improvisar actividades sobre la marcha', 'Sentir que nunca llegás con todo'].map((text, i) => <div key={i} className="flex items-start gap-3" data-id="element-99">
                    <X className="w-5 h-5 text-[#C75B39] mt-0.5 flex-shrink-0" data-id="element-100" />
                    <span className="text-gray-600 text-sm leading-relaxed" data-id="element-101">
                      {text}
                    </span>
                  </div>)}
              </div>
            </motion.div>

            {/* After */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-emerald-50/50 rounded-2xl p-8 border border-emerald-100 relative" data-id="element-102">
              <div className="absolute top-4 right-4 bg-[#2D7A65]/10 text-[#2D7A65] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-103">
                Con el MegaPack
              </div>
              <div className="space-y-4 mt-6" data-id="element-104">
                {['Abrís la carpeta, elegís y listo: clase resuelta', 'Evaluaciones listas con pauta incluida', 'Rúbricas claras: corregís en la mitad de tiempo', 'Actividades probadas que funcionan de verdad', 'Llegás tranquila al aula, con todo preparado'].map((text, i) => <div key={i} className="flex items-start gap-3" data-id="element-105">
                    <Check className="w-5 h-5 text-[#2D7A65] mt-0.5 flex-shrink-0" data-id="element-106" />
                    <span className="text-gray-700 text-sm leading-relaxed font-medium" data-id="element-107">
                      {text}
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-gradient-to-b from-white to-[#FDF8F3] px-4 border-y border-gray-100" data-id="element-108">
        <div className="max-w-6xl mx-auto" data-id="element-109">
          <div className="text-center mb-20" data-id="element-110">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2D7A65] mb-6" data-id="element-111">
              Imaginate esto:
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto font-light" data-id="element-112">
              Abrís una carpeta. Elegís el tema. Imprimís. Y ya tenés la clase
              resuelta.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-113">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-114">
              <div className="absolute inset-0 bg-emerald-100 rounded-3xl transform -rotate-3 scale-105" data-id="element-115"></div>
              <img src={IMAGES.cover} alt="Interior del pack" className="relative w-full rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] transform transition duration-700 hover:scale-[1.02]" data-id="element-116" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-117">
              <h3 className="font-heading text-3xl font-bold text-[#2D2A26] mb-6 leading-tight" data-id="element-118">
                MegaPack Lengua y Literatura Lista
              </h3>
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-[#2D7A65] font-semibold px-4 py-2 rounded-lg text-sm mb-8 border border-emerald-100" data-id="element-119">
                <BookOpen className="w-4 h-4" data-id="element-120" /> Incluye 5 tomos completos
              </div>

              <div className="prose prose-lg text-gray-600 mb-10" data-id="element-121">
                <p data-id="element-122">
                  Material digital listo para imprimir y usar, creado para
                  docentes que necesitan ahorrar tiempo, enseñar con más
                  claridad y tener un año organizado desde el día 1.
                </p>
                <p data-id="element-123">
                  No es "un PDF más". Es un sistema completo para que Lengua
                  deje de ser una carga y vuelva a ser una materia que puedas
                  enseñar con tranquilidad.
                </p>
              </div>

              <div className="space-y-5" data-id="element-124">
                {['Abrís, elegís tema y la clase sale en minutos', 'Consignas pensadas para que el estudiante produzca', 'Corregís con rúbricas claras (menos desgaste)'].map((text, i) => <div key={i} className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100 transition hover:shadow-md" data-id="element-125">
                    <div className="bg-emerald-50 p-2 rounded-full flex-shrink-0" data-id="element-126">
                      <Check className="w-5 h-5 text-[#2D7A65]" data-id="element-127" />
                    </div>
                    <span className="font-medium text-[#2D2A26]" data-id="element-128">{text}</span>
                  </div>)}
              </div>

              <div className="mt-10" data-id="element-129">
                <button onClick={scrollToPurchase} className="bg-[#C75B39] hover:bg-[#A63E28] text-white font-bold py-4 px-8 rounded-xl shadow-[0_8px_24px_rgba(199,91,57,0.25)] transform transition hover:-translate-y-1 text-lg inline-flex items-center justify-center gap-2 border-b-4 border-[#8B3A22] cursor-pointer" data-id="element-130">
                  Ver la oferta completa
                  <Check className="w-5 h-5" data-id="element-131" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* NUEVO: "¿Para quién es?" Section — before Modules */}
      <section className="py-20 px-4 bg-[#FDF8F3] border-y border-[#E8D5BE]" data-id="element-132">
        <div className="max-w-4xl mx-auto text-center" data-id="element-133">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2D2A26] mb-4" data-id="element-134">
            Este MegaPack es para vos si…
          </h2>
          <p className="text-gray-500 mb-12 text-lg" data-id="element-135">
            Marcá mentalmente cuántas te identifican:
          </p>

          <div className="grid sm:grid-cols-2 gap-4 text-left max-w-3xl mx-auto" data-id="element-136">
            {['Enseñás Lengua y Literatura (primaria o secundaria)', 'Perdés horas buscando textos y armando consignas', 'Querés evaluar con criterios claros, no "a ojo"', 'Necesitás material listo, no "ideas para adaptar"', 'Te gustaría tener el año planificado desde marzo', 'Valorás tu tiempo y querés invertirlo en enseñar, no en buscar'].map((text, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm" data-id="element-137">
                <div className="bg-[#E8A838]/15 p-1 rounded-full mt-0.5 flex-shrink-0" data-id="element-138">
                  <Check className="w-4 h-4 text-[#D4922A]" data-id="element-139" />
                </div>
                <span className="text-[#2D2A26] text-sm leading-relaxed" data-id="element-140">
                  {text}
                </span>
              </motion.div>)}
          </div>

          <p className="mt-10 text-[#2D7A65] font-semibold text-lg" data-id="element-141">
            Si marcaste 3 o más, este pack fue pensado para vos.
          </p>
          <button onClick={scrollToPurchase} className="mt-6 text-[#C75B39] hover:text-[#A63E28] font-bold text-lg inline-flex items-center gap-2 transition group cursor-pointer" data-id="element-142">
            Ver la oferta
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-143" />
          </button>
        </div>
      </section>

      {/* What's Included (Modules) */}
      <section className="py-24 px-4 bg-white" data-id="element-144">
        <div className="max-w-6xl mx-auto" data-id="element-145">
          <div className="text-center mb-20" data-id="element-146">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2D2A26] mb-6" data-id="element-147">
              Lo que incluye el MegaPack
            </h2>
            <p className="text-xl text-[#2D7A65] font-medium" data-id="element-148">
              (lo que realmente usás en el aula)
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 md:gap-8" data-id="element-149">
            {MODULES.map((module, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] bg-white rounded-2xl p-8 border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition duration-300 hover:border-emerald-200 group" data-id="element-150">
                <div className="bg-emerald-50 w-14 h-14 rounded-xl flex items-center justify-center text-[#2D7A65] mb-6 group-hover:bg-[#2D7A65] group-hover:text-white transition-colors duration-300" data-id="element-151">
                  {module.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-[#2D2A26] mb-5 min-h-[56px] leading-snug" data-id="element-152">
                  {module.title}
                </h3>
                <ul className="space-y-3" data-id="element-153">
                  {module.items.map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-gray-600 leading-relaxed" data-id="element-154">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E8A838] flex-shrink-0" data-id="element-155"></div>
                      {item}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>

          <div className="mt-20 text-center bg-[#F5F0EA] p-10 rounded-3xl border border-[#E8D5BE] max-w-4xl mx-auto relative overflow-hidden" data-id="element-156">
            <div className="absolute top-0 left-0 w-2 h-full bg-[#2D7A65]" data-id="element-157"></div>
            <h3 className="font-heading text-2xl font-bold text-[#2D2A26] mb-4" data-id="element-158">
              "Ok, pero… ¿sirve para mi grado/año?"
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto" data-id="element-159">
              Sí. Este pack está pensado para{' '}
              <span className="font-bold text-[#2D7A65]" data-id="element-160">
                primaria y secundaria
              </span>
              , y el material es adaptable. Lo importante no es que el documento
              diga "3° A", sino que tengas textos seleccionados, consignas
              claras y estructura didáctica.
            </p>
            <button onClick={scrollToPurchase} className="mt-8 bg-[#2D7A65] hover:bg-[#1B5E4B] text-white font-bold py-4 px-8 rounded-xl shadow-lg transform transition hover:-translate-y-1 text-lg inline-flex items-center justify-center gap-2 cursor-pointer" data-id="element-161">
              <BookOpen className="w-5 h-5" data-id="element-162" />
              Quiero organizar mi año
            </button>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-24 bg-gradient-to-br from-[#1B5E4B] via-[#2D7A65] to-[#1B5E4B] text-white px-4 relative overflow-hidden" data-id="element-163">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#E8A838] opacity-5 rounded-full blur-[120px]" data-id="element-164"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white opacity-5 rounded-full blur-[100px]" data-id="element-165"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-166">
          <div className="text-center mb-20" data-id="element-167">
            <span className="bg-[#C75B39] text-white font-bold px-5 py-1.5 rounded-full text-sm uppercase tracking-wider mb-6 inline-block shadow-lg" data-id="element-168">
              Solo por hoy
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-id="element-169">
              Bonos incluidos GRATIS
            </h2>
            <p className="text-xl text-emerald-100 font-light max-w-2xl mx-auto" data-id="element-170">
              Para que el año quede aún más resuelto. Recursos adicionales de
              alto valor.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-171">
            {BONUSES.map((bonus, idx) => <motion.div key={idx} whileHover={{
            y: -10
          }} className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 flex flex-col hover:border-white/30 transition-all duration-300 shadow-xl" data-id="element-172">
                <div className="relative mb-6 group" data-id="element-173">
                  <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg z-10" data-id="element-174">
                    GRATIS
                  </div>
                  <div className="overflow-hidden rounded-xl" data-id="element-175">
                    <img src={bonus.image} alt={bonus.title} className="w-full rounded-xl shadow-lg transform group-hover:scale-105 transition duration-500" data-id="element-176" />
                  </div>
                </div>
                <h3 className="font-heading text-xl font-bold text-[#E8A838] mb-3 leading-tight" data-id="element-177">
                  {bonus.title}
                </h3>
                <p className="text-sm text-emerald-50 mb-6 flex-grow leading-relaxed opacity-90" data-id="element-178">
                  {bonus.desc}
                </p>
                <div className="bg-white/10 rounded-lg px-4 py-3 text-sm font-medium text-center text-white border border-white/10" data-id="element-179">
                  Incluye: {bonus.value}
                </div>
              </motion.div>)}
          </div>

          <div className="text-center mt-16" data-id="element-180">
            <button onClick={scrollToPurchase} className="bg-[#E8A838] hover:bg-[#D4922A] text-[#1B5E4B] font-bold py-5 px-10 rounded-xl shadow-[0_8px_30px_rgba(232,168,56,0.3)] transform transition hover:-translate-y-1 text-xl inline-flex items-center justify-center gap-3 cursor-pointer" data-id="element-181">
              Acceder al MegaPack + Todos los Bonos
              <Check className="w-6 h-6" data-id="element-182" />
            </button>
            <p className="mt-4 text-sm text-emerald-200/70 font-medium" data-id="element-183">
              Oferta por tiempo limitado
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section — ENHANCED with value stack */}
      <section id="compra" className="py-24 px-4 bg-gradient-to-b from-[#FDF8F3] to-white relative overflow-hidden" data-id="element-184">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#e5e0d8_1px,transparent_1px)] [background-size:24px_24px] opacity-40" data-id="element-185"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-186">
          <div className="text-center mb-16" data-id="element-187">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2D2A26] mb-6" data-id="element-188">
              Todo lo que vas a recibir
            </h2>
            <p className="text-xl text-gray-600 font-light" data-id="element-189">
              El MegaPack completo con todos los bonos incluidos
            </p>
          </div>

          {/* Book Fan Layout */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="relative flex items-center justify-center h-[280px] md:h-[450px] mb-20 max-w-4xl mx-auto" data-id="element-190">
            <div className="absolute inset-0 bg-[#E8A838] opacity-10 blur-[100px] rounded-full" data-id="element-191"></div>
            <img src={IMAGES.bono1} alt="Bono 1" className="absolute w-28 md:w-44 z-10 transform -rotate-12 -translate-x-32 md:-translate-x-64 drop-shadow-xl transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-192" />
            <img src={IMAGES.bono2} alt="Bono 2" className="absolute w-32 md:w-52 z-20 transform -rotate-6 -translate-x-20 md:-translate-x-36 drop-shadow-2xl transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-193" />
            <img src={IMAGES.cover} alt="Main Cover" className="relative w-40 md:w-64 z-30 drop-shadow-[0_30px_60px_rgba(0,0,0,0.4)] transition-transform hover:-translate-y-6 hover:scale-105 duration-500" data-id="element-194" />
            <img src={IMAGES.bono3} alt="Bono 3" className="absolute w-32 md:w-52 z-20 transform rotate-6 translate-x-20 md:translate-x-36 drop-shadow-2xl transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-195" />
            <img src={IMAGES.bono4} alt="Bono 4" className="absolute w-28 md:w-44 z-10 transform rotate-12 translate-x-32 md:translate-x-64 drop-shadow-xl transition-transform hover:-translate-y-6 hover:z-40 duration-500" data-id="element-196" />
          </motion.div>

          {/* Pricing Card — ENHANCED */}
          <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] overflow-hidden border border-gray-100 max-w-4xl mx-auto relative" data-id="element-197">
            <div className="absolute top-0 left-0 w-full h-2 bg-[#E8A838]" data-id="element-198"></div>

            <div className="bg-gradient-to-r from-[#1B5E4B] to-[#2D7A65] p-8 md:p-10 text-center text-white relative overflow-hidden mt-2" data-id="element-199">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl" data-id="element-200"></div>
              <div className="inline-flex items-center gap-2 bg-[#E8A838] text-[#1B5E4B] font-bold px-4 py-1.5 rounded-full text-sm mb-4 shadow-lg" data-id="element-201">
                <Sparkles className="w-4 h-4" data-id="element-202" />
                MEJOR OFERTA
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold relative z-10" data-id="element-203">
                Oferta Especial Limitada
              </h2>
              <p className="text-lg text-emerald-100 relative z-10 mt-3 font-light" data-id="element-204">
                Asegurá tu acceso antes de que el contador llegue a cero
              </p>
            </div>

            <div className="p-8 md:p-14" data-id="element-205">
              {/* Value Stack */}
              <div className="max-w-2xl mx-auto mb-10" data-id="element-206">
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest text-center mb-6" data-id="element-207">
                  Valor real de todo lo incluido
                </p>
                <div className="space-y-3" data-id="element-208">
                  {VALUE_STACK.map((item, i) => <div key={i} className={`flex items-center justify-between py-2.5 px-4 rounded-lg ${item.isBonus ? 'bg-amber-50/60 border border-amber-100/60' : item.highlight ? 'bg-emerald-50/60 border border-emerald-100' : 'border-b border-gray-100'}`} data-id="element-209">
                      <div className="flex items-center gap-3" data-id="element-210">
                        <Check className={`w-4 h-4 flex-shrink-0 ${item.isBonus ? 'text-[#D4922A]' : 'text-[#2D7A65]'}`} data-id="element-211" />
                        <span className={`text-sm ${item.highlight ? 'font-bold text-[#2D2A26]' : item.isBonus ? 'font-semibold text-[#2D2A26]' : 'text-gray-700'}`} data-id="element-212">
                          {item.isBonus && <span className="text-[#D4922A] mr-1" data-id="element-213">🎁</span>}
                          {item.label}
                        </span>
                      </div>
                      <span className="text-gray-400 line-through text-sm font-medium tabular-nums flex-shrink-0 ml-4" data-id="element-214">
                        {formatPrice(item.value)}
                      </span>
                    </div>)}
                </div>

                {/* Total Value */}
                <div className="mt-4 flex items-center justify-between py-3 px-4 bg-gray-900 rounded-xl text-white" data-id="element-215">
                  <span className="font-bold text-lg" data-id="element-216">Valor total:</span>
                  <span className="font-bold text-xl line-through opacity-70 tabular-nums" data-id="element-217">
                    {formatPrice(totalValue)}
                  </span>
                </div>
              </div>

              {/* Price Display */}
              <div className="text-center" data-id="element-218">
                <div className="flex flex-col items-center mb-6" data-id="element-219">
                  {!currencyLoading && !isUSD && <div className="flex flex-col items-center mb-4" data-id="element-220">
                      <span className="text-4xl mb-2" role="img" aria-label="Bandera de tu país" data-id="element-221">
                        {countryFlag}
                      </span>
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest" data-id="element-222">
                        Precio en tu moneda
                      </span>
                    </div>}

                  <p className="text-sm text-gray-500 font-medium mb-2" data-id="element-223">
                    Hoy lo llevás por:
                  </p>

                  <div className="text-gray-400 line-through text-2xl md:text-3xl font-medium mb-2" data-id="element-224">
                    {currencyLoading ? '$47.00' : formatPrice(47)}
                  </div>

                  <div className="relative inline-block" data-id="element-225">
                    <div className="text-5xl md:text-7xl font-extrabold text-[#C75B39] tracking-tight leading-none" data-id="element-226">
                      {currencyLoading ? '$13,99' : formatPrice(13.99)}
                    </div>
                    {/* Savings Badge */}
                    <div className="absolute -top-3 -right-16 md:-right-20 bg-[#E8A838] text-[#1B5E4B] font-extrabold text-xs md:text-sm px-3 py-1.5 rounded-full shadow-lg transform rotate-6 whitespace-nowrap" data-id="element-227">
                      -{savingsPercent}%
                    </div>
                  </div>
                </div>

                {!currencyLoading && !isUSD && <p className="text-xs text-gray-400 mb-4" data-id="element-228">
                    Precio aproximado en {currencyCode} · El cobro final se
                    realiza en USD
                  </p>}

                <p className="text-[#C75B39] font-bold text-lg mb-10 animate-pulse bg-orange-50 inline-block px-6 py-2 rounded-full" data-id="element-229">
                  🔥 Quedan pocas unidades a este precio
                </p>

                {/* Inline Countdown */}
                <div className="mb-8 flex flex-col items-center justify-center" data-id="element-230">
                  <p className="text-sm text-gray-500 font-semibold mb-3 uppercase tracking-widest" data-id="element-231">
                    La oferta expira en:
                  </p>
                  <div className="flex items-center gap-3" data-id="element-232">
                    <div className="bg-[#1B5E4B] text-white rounded-xl px-5 py-3 text-3xl font-bold tabular-nums shadow-inner" data-id="element-233">
                      {String(minutes).padStart(2, '0')}
                    </div>
                    <span className="text-3xl font-bold text-gray-400" data-id="element-234">:</span>
                    <div className="bg-[#1B5E4B] text-white rounded-xl px-5 py-3 text-3xl font-bold tabular-nums shadow-inner" data-id="element-235">
                      {String(seconds).padStart(2, '0')}
                    </div>
                  </div>
                </div>

                <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="block w-full max-w-xl mx-auto bg-[#C75B39] hover:bg-[#A63E28] text-white font-bold py-6 px-8 rounded-2xl shadow-[0_10px_30px_rgba(199,91,57,0.3)] transform transition hover:-translate-y-1 text-xl md:text-2xl mb-4 border-b-4 border-[#8B3A22]" data-id="element-236">
                  SÍ, QUIERO ACCEDER AL MEGAPACK
                </a>

                <p className="text-xs text-gray-400 mb-8" data-id="element-237">
                  Pago único · Sin suscripción · Acceso de por vida
                </p>

                {/* Payment Methods & Security */}
                <div className="space-y-6" data-id="element-238">
                  <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm text-gray-600 font-medium" data-id="element-239">
                    <span className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-gray-200 shadow-sm" data-id="element-240">
                      <ShieldCheck className="w-4 h-4 text-[#2D7A65]" data-id="element-241" /> Pago
                      100% Seguro
                    </span>
                    <span className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-gray-200 shadow-sm" data-id="element-242">
                      <Download className="w-4 h-4 text-[#2D7A65]" data-id="element-243" /> Entrega
                      Inmediata
                    </span>
                    <span className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-gray-200 shadow-sm" data-id="element-244">
                      <Star className="w-4 h-4 text-[#E8A838] fill-current" data-id="element-245" />{' '}
                      Garantía 7 Días
                    </span>
                  </div>

                  {/* Payment methods */}
                  <div className="flex items-center justify-center gap-3 pt-4 border-t border-gray-100" data-id="element-246">
                    <span className="text-xs text-gray-400 mr-2" data-id="element-247">
                      Medios de pago:
                    </span>
                    <div className="flex items-center gap-2" data-id="element-248">
                      {['Visa', 'Mastercard', 'Amex', 'PayPal'].map(method => <span key={method} className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-249">
                            {method}
                          </span>)}
                    </div>
                  </div>

                  {/* Platform trust */}
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-400" data-id="element-250">
                    <Lock className="w-3.5 h-3.5" data-id="element-251" />
                    <span data-id="element-252">
                      Compra protegida · Procesado por{' '}
                      <strong className="text-gray-500" data-id="element-253">Hotmart</strong> ·
                      Encriptación SSL
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white px-4 border-t border-gray-100" data-id="element-254">
        <div className="max-w-6xl mx-auto" data-id="element-255">
          <h2 className="font-heading text-4xl font-bold text-center text-[#2D2A26] mb-16" data-id="element-256">
            Docentes que ya lo usan
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-8" data-id="element-257">
            {TESTIMONIALS.map((t, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className={`bg-[#FDF8F3] p-8 rounded-2xl border border-[#E8D5BE] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 ${idx < 3 ? 'md:col-span-2' : 'md:col-span-3'}`} data-id="element-258">
                <div className="flex items-center gap-1 text-[#E8A838] mb-6" data-id="element-259">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-current" data-id="element-260" />)}
                </div>
                <p className="text-gray-700 mb-8 italic text-lg leading-relaxed" data-id="element-261">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-4 mt-auto" data-id="element-262">
                  <div className="w-12 h-12 bg-[#2D7A65] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm" data-id="element-263">
                    {t.name.substring(0, 2)}
                  </div>
                  <div data-id="element-264">
                    <p className="font-bold text-[#2D2A26]" data-id="element-265">{t.name}</p>
                    <p className="text-sm text-gray-500" data-id="element-266">
                      {t.role} • {t.location}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center mt-16" data-id="element-267">
            <button onClick={scrollToPurchase} className="bg-[#C75B39] hover:bg-[#A63E28] text-white font-bold py-5 px-10 rounded-xl shadow-[0_10px_30px_rgba(199,91,57,0.25)] transform transition hover:-translate-y-1 text-xl inline-flex items-center justify-center gap-3 border-b-4 border-[#8B3A22] cursor-pointer" data-id="element-268">
              Sí, quiero el MegaPack
              <Check className="w-6 h-6" data-id="element-269" />
            </button>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-24 px-4 bg-white" data-id="element-270">
        <div className="max-w-4xl mx-auto" data-id="element-271">
          <div className="bg-gradient-to-br from-[#F5F0EA] to-white p-10 md:p-16 rounded-3xl border border-[#E8D5BE] shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center relative overflow-hidden" data-id="element-272">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#E8A838] opacity-10 rounded-full blur-2xl" data-id="element-273"></div>
            <div className="w-24 h-24 bg-white border border-gray-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg relative z-10" data-id="element-274">
              <ShieldCheck className="w-12 h-12 text-[#2D7A65]" data-id="element-275" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-[#2D2A26] mb-6 relative z-10" data-id="element-276">
              Garantía de Satisfacción Total
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed relative z-10" data-id="element-277">
              Queremos que lo compres con total tranquilidad. Tenés{' '}
              <strong className="text-[#2D2A26]" data-id="element-278">7 días de garantía</strong>: si
              lo descargás y sentís que no te sirve para tu realidad docente,
              pedís el reembolso completo. Sin preguntas, sin vueltas.
            </p>
            <button onClick={scrollToPurchase} className="bg-[#2D7A65] hover:bg-[#1B5E4B] text-white font-bold py-4 px-8 rounded-xl shadow-lg transform transition hover:-translate-y-1 text-lg inline-flex items-center justify-center gap-2 cursor-pointer relative z-10" data-id="element-279">
              <ShieldCheck className="w-5 h-5" data-id="element-280" />
              Comprar sin riesgo
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#FDF8F3] px-4 border-t border-[#E8D5BE]" data-id="element-281">
        <div className="max-w-3xl mx-auto" data-id="element-282">
          <h2 className="font-heading text-4xl font-bold text-center text-[#2D2A26] mb-16" data-id="element-283">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4" data-id="element-284">
            {FAQS.map((faq, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-[#E8D5BE] overflow-hidden transition-shadow hover:shadow-md" data-id="element-285">
                <details className="group" data-id="element-286">
                  <summary className="flex justify-between items-center font-medium cursor-pointer list-none p-6 md:p-8" data-id="element-287">
                    <span className="text-lg text-[#2D2A26] pr-4" data-id="element-288">{faq.q}</span>
                    <span className="transition duration-300 group-open:rotate-180 bg-emerald-50 p-2 rounded-full flex-shrink-0" data-id="element-289">
                      <ChevronDown className="w-5 h-5 text-[#2D7A65]" data-id="element-290" />
                    </span>
                  </summary>
                  <div className="text-gray-600 px-6 md:px-8 pb-6 md:pb-8 pt-0 leading-relaxed" data-id="element-291">
                    {faq.a}
                  </div>
                </details>
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#1B5E4B] to-[#2D7A65] text-white px-4 text-center relative overflow-hidden" data-id="element-292">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#E8A838] opacity-10 rounded-full blur-[100px]" data-id="element-293"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-294">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-295">
            Planificar Lengua no debería sentirse como cargar una mochila extra
          </h2>
          <p className="text-xl md:text-2xl text-emerald-100 mb-12 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-296">
            Este MegaPack te promete algo real: orden, claridad y horas
            recuperadas. Para que puedas enseñar mejor y vivir el año con más
            tranquilidad.
          </p>

          <button onClick={scrollToPurchase} className="bg-[#E8A838] hover:bg-[#D4922A] text-[#1B5E4B] font-bold py-5 px-10 rounded-xl shadow-[0_8px_30px_rgba(232,168,56,0.3)] transform transition hover:-translate-y-1 text-xl inline-flex items-center justify-center gap-3 mx-auto cursor-pointer" data-id="element-297">
            Sí, quiero el MegaPack Lengua y Literatura
            <Check className="w-6 h-6" data-id="element-298" />
          </button>
          <p className="mt-8 text-sm text-emerald-200 font-medium tracking-wide uppercase" data-id="element-299">
            Acceso inmediato + Bonos incluidos hoy
          </p>
        </div>
      </section>

      {/* ENHANCED Footer */}
      <footer className="bg-[#1A2E28] text-[#8BA69C] py-12 text-center text-sm" data-id="element-300">
        <div className="max-w-4xl mx-auto px-4 space-y-4" data-id="element-301">
          <div className="flex items-center justify-center gap-2 text-[#8BA69C]/80 mb-6" data-id="element-302">
            <Lock className="w-4 h-4" data-id="element-303" />
            <span data-id="element-304">
              Sitio protegido con encriptación SSL · Compra 100% segura
            </span>
          </div>
          <p data-id="element-305">© 2026 MegaPack Docente. Todos los derechos reservados.</p>
          <p className="opacity-70 max-w-2xl mx-auto leading-relaxed" data-id="element-306">
            Este producto es un material digital descargable. Al realizar la
            compra, recibirás acceso inmediato al contenido completo. Procesado
            de forma segura por Hotmart, plataforma líder en productos digitales
            con millones de transacciones realizadas.
          </p>
          <p className="opacity-50" data-id="element-307">
            Este sitio no forma parte de Facebook, Google ni de ninguna de sus
            empresas asociadas.
          </p>
        </div>
      </footer>
    </div>;
}